/* eslint-disable no-restricted-syntax */
// node modules
import cloneDeep from 'lodash/cloneDeep';
// modules
import { WsLiveV2Feeder } from 'modules/WebSocket';
import Logger from 'modules/Logger';
import SearchService from 'modules/Search';
import FormatService from 'modules/Format';
// store
import { LTPC, FullFeed, OptionChain, Quote } from 'app/marketDataFeed';
import {
  QuoteData,
  QuotesCallback,
  DOMCallback,
  DOMData,
  DOMLevel,
} from 'modules/TradingChart/components/TradingView/sources/charting_library.min';
import { DOMDetails, MarketFeederV2 } from 'modules/LiveData/typings';
import { broker } from 'modules/TradingChart/consts';
import TradingChartService from 'modules/TradingChart/service';
import { decomposeExchangeToken } from 'modules/BOD/modules/Search/utils';
// constants
import TokenMapper from 'modules/TokenMapper';
import { ACTIVE_WATCHLIST_ID, SAVED_WATCHLIST_SYMBOLS } from 'modules/Watchlists/constants';
import { ChangeModeSubscriptionData } from 'modules/TokenMapper/typings';
import { DEFAULT_CHANGE_MODE_INSTRUMENT_KEYS } from 'modules/TokenMapper/constants';
import {
  INITIAL_QUEUE_COLLECTION,
  SUBSCRIPTION_MODES,
  MARKET_FEEDER_MODES,
  DEFAULT_DOM_DETAILS,
} from './constants';
// store
import LiveDataStore from './store';

class LiveDataService extends LiveDataStore {
  inactiveTimeout: NodeJS.Timeout | number | null = null;
  inactiveTimeoutTime = 10000;

  constructor() {
    super();

    WsLiveV2Feeder.registerHandler('initial_feed', this.handleLiveDataResponse);
    WsLiveV2Feeder.registerHandler('live_feed', this.handleLiveDataResponse);
    WsLiveV2Feeder.registerHandler('ws_connect', this.checkForTokensInQueue);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    WsLiveV2Feeder.registerHandler('market_info', () => { });

    // Bind methods to avoid issues with `this`
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    // Add event listeners
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('blur', this.handleVisibilityChange);
  }

  private handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // clear timeout if already set
      if (this.inactiveTimeout) {
        clearTimeout(this.inactiveTimeout);
        this.inactiveTimeout = null;
      }
      // subscribe to all modes
      const toSubScribeOnActive = cloneDeep(
        DEFAULT_CHANGE_MODE_INSTRUMENT_KEYS,
      );
      for (const [key, value] of TokenMapper.tokenMapper.entries()) {
        const feederModes = Object.keys(value) as MARKET_FEEDER_MODES[];
        const unsubAndSubAllowed = feederModes.find(
          mode => value[mode]?.unsubAndSubAllowed,
        );
        if (!unsubAndSubAllowed) {
          const mode = TokenMapper.chooseMode(value);
          toSubScribeOnActive[mode].push(key);
        }
      }
      for (
        const mode of Object.keys(toSubScribeOnActive) as MARKET_FEEDER_MODES[]
      ) {
        this.subscribeScripWhenActive(toSubScribeOnActive[mode], mode);
      }
    } else {
      // clear timeout if already set
      if (this.inactiveTimeout) {
        clearTimeout(this.inactiveTimeout);
        this.inactiveTimeout = null;
      }
      // set timeout to unsubscribe from all modes
      this.inactiveTimeout = setTimeout(() => {
        this.inactiveTimeout = null;
        const toUnsubscribeOnInactive = cloneDeep(
          DEFAULT_CHANGE_MODE_INSTRUMENT_KEYS,
        );
        // Iterate over tokenMapper and populate modeKeysMap
        for (const [key, value] of TokenMapper.tokenMapper.entries()) {
          const feederModes = Object.keys(value) as MARKET_FEEDER_MODES[];
          const unsubAndSubAllowed = feederModes
            .find(mode => value[mode]?.unsubAndSubAllowed);
          if (!unsubAndSubAllowed) {
            const higestMode = TokenMapper.chooseMode(value);
            toUnsubscribeOnInactive[higestMode].push(key);
          }
        }
        // Iterate through the typed keys of modeKeysMap
        for (
          const mode of Object.keys(
            toUnsubscribeOnInactive,
          ) as MARKET_FEEDER_MODES[]
        ) {
          this.unsubscribeScripWhenInactive(
            toUnsubscribeOnInactive[mode],
            mode,
          );
        }
      }, this.inactiveTimeoutTime);
    }
  }

  unsubscribeScripWhenInactive = (
    unsubscribedInstrumentKeys: string[],
    mode: MARKET_FEEDER_MODES,
    guid?: string,
  ) => {
    if (!WsLiveV2Feeder.connected) {
      this.setTokensInQueue(
        mode,
        [...unsubscribedInstrumentKeys],
        false,
      );
      return;
    }

    if (unsubscribedInstrumentKeys.length > 0) {
      this.unsubscribeScripFeed(unsubscribedInstrumentKeys, mode, guid);
      this.cleanupSubscriptionData(unsubscribedInstrumentKeys);
    }
  }

  subscribeScripWhenActive = (
    subscribedInstrumentKeys: string[],
    mode: MARKET_FEEDER_MODES,
    guid?: string,
  ) => {
    if (!WsLiveV2Feeder.connected) {
      this.setTokensInQueue(
        mode,
        [...subscribedInstrumentKeys],
        true,
      );
      return;
    }

    if (subscribedInstrumentKeys.length > 0) {
      this.subscribeScripFeed(subscribedInstrumentKeys, mode, guid);
    }
  }

  quotesData: { [x: string]: QuotesCallback } = {};

  domDetails: DOMDetails = DEFAULT_DOM_DETAILS;

  subscribeTokensInQueue: Record<MARKET_FEEDER_MODES, string[]> = {
    ...INITIAL_QUEUE_COLLECTION,
  };

  unsubscribeTokensInQueue: Record<MARKET_FEEDER_MODES, string[]> = {
    ...INITIAL_QUEUE_COLLECTION,
  };

  ltpcTokens: string[] = [];

  public resetDomDetails() {
    this.domDetails = DEFAULT_DOM_DETAILS;
  }

  setDomDetails = (symbol: string, callback: DOMCallback): void => {
    this.domDetails = {
      symbol,
      callback,
    };
  };

  /**
 * Clean up event listeners and resources
 */
  destroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  resubscribeToWatchlistSymbols = async () => {
    const activeWatchlist: {
      id?: string;
      title?: string;
    } = JSON.parse(
      window.localStorage.getItem(ACTIVE_WATCHLIST_ID) || '{}',
    );
    const activeWatchlistDetails: {
      symbols?: string[];
    } = JSON.parse(
      window.localStorage.getItem(
        `${SAVED_WATCHLIST_SYMBOLS}${activeWatchlist?.id}`,
      ) || '{}',
    );

    // subscribe to instrument keys in live data on reconnect
    if (activeWatchlistDetails?.symbols?.length) {
      const items = activeWatchlistDetails?.symbols?.map((item: string) =>
        FormatService.decodeCustomTvExchangeToken(item),
      );
      const tokensToSubscribe = await this.getSymbolInstrumentKeys(items);
      this.subscribeScrip(tokensToSubscribe, MARKET_FEEDER_MODES.LTPC);
    }
  }

  checkForTokensInQueue = () => {
    SUBSCRIPTION_MODES.forEach(mode => {
      const subscribeTokens = this.subscribeTokensInQueue[mode];
      const unsubscribeTokens = this.unsubscribeTokensInQueue[mode];

      if (subscribeTokens.length) {
        this.subscribeScripFeed(subscribeTokens, mode);
      }

      if (unsubscribeTokens.length) {
        this.unsubscribeScripFeed(subscribeTokens, mode);
      }
    });
  };

  setTokensInQueue = (
    mode: MARKET_FEEDER_MODES,
    tokensInQueue: string[],
    isSub: boolean,
  ) => {
    if (isSub) {
      this.subscribeTokensInQueue[mode] = tokensInQueue;
    } else {
      this.unsubscribeTokensInQueue[mode] = tokensInQueue;
    }
  };

  removeTokensFromQueue = (
    dataFormat: MARKET_FEEDER_MODES,
    isSub?: boolean,
  ) => {
    if (isSub) {
      this.subscribeTokensInQueue[dataFormat] = [];
    } else {
      this.unsubscribeTokensInQueue[dataFormat] = [];
    }
  };

  getSymbolInstrumentKeys = (symbols: string[]) =>
    Promise.all(
      symbols.map(async symbol => {
        const scripData = await SearchService.searchByExchangeToken(symbol);

        return scripData?.instrumentKey || '';
      }),
    );

  subscribeScripFeed = (
    instrumentKeys: string[],
    mode: MARKET_FEEDER_MODES,
    guid: string = FormatService.generateUUID(),
  ) => {
    const request: MarketFeederV2.SubscriptionRequest = {
      guid,
      method: 'sub',
      data: {
        instrumentKeys,
        mode,
      },
    };

    Logger.log(
      'WS',
      `Subscribing to scrips (${mode}): ${instrumentKeys} (${instrumentKeys.length})`,
    );

    WsLiveV2Feeder.send(request);

    // remove tokens from queue
    this.removeTokensFromQueue(mode, true);
  };

  unsubscribeScripFeed = (
    instrumentKeys: string[],
    mode: MARKET_FEEDER_MODES,
    guid?: string,
  ) => {
    const request: MarketFeederV2.SubscriptionRequest = {
      guid,
      method: 'unsub',
      data: {
        instrumentKeys,
        mode,
      },
    };

    Logger.log(
      'WS',
      `Unsubscribing to scrips (${mode}): ${instrumentKeys} (${instrumentKeys.length})`,
    );

    WsLiveV2Feeder.send(request);

    // remove tokens from queue
    this.removeTokensFromQueue(mode);

    // remove tokens from live data
    this.deleteLiveDataTokens(instrumentKeys);
  };

  changeScripFeedMode = (
    instrumentKeys: string[],
    mode: MARKET_FEEDER_MODES,
    guid: string = FormatService.generateUUID(),
  ) => {
    const request: MarketFeederV2.SubscriptionRequest = {
      guid,
      method: 'change_mode',
      data: {
        instrumentKeys,
        mode,
      },
    };

    Logger.log(
      'WS',
      `Change mode for scrips (${mode}): ${instrumentKeys} (${instrumentKeys.length})`,
    );

    WsLiveV2Feeder.send(request);

    // update subscription mode for tokens
    this.updateSubscriptionMode(mode, instrumentKeys);
  };

  subscribeScrip = (
    instrumentKeys: string[],
    mode: MARKET_FEEDER_MODES,
    guid?: string,
    unsubAndSubAllowed?: boolean,
  ) => {
    const {
      subscribedInstrumentKeys,
      changeModeInstrumentKeys,
    } = TokenMapper.addInstrumentKeys(instrumentKeys, mode, unsubAndSubAllowed);

    // if not connected, add to queue
    if (!WsLiveV2Feeder.connected) {
      this.setTokensInQueue(
        mode,
        [...subscribedInstrumentKeys, ...changeModeInstrumentKeys[mode]],
        true,
      );
      return;
    }

    if (subscribedInstrumentKeys.length > 0) {
      this.subscribeScripFeed(subscribedInstrumentKeys, mode, guid);
    }

    this.changeScripSubscriptionFeed(
      changeModeInstrumentKeys,
      guid,
    );
  };

  private cleanupSubscriptionData(instrumentKeys: string[]) {
    // Clear references that might prevent GC
    instrumentKeys.forEach(key => {
      delete this.quotesData[key];
      // Clear any other cached data
    });
  }

  unsubscribeScrip = (
    instrumentKeys: string[],
    mode: MARKET_FEEDER_MODES,
    guid: string = FormatService.generateUUID(),
    unsubAndSubAllowed?: boolean,
  ) => {
    const {
      unsubscribedInstrumentKeys,
      changeModeInstrumentKeys,
    } = TokenMapper.removeInstrumentKeys(
      instrumentKeys,
      mode,
      unsubAndSubAllowed,
    );

    // if not connected, add to queue
    if (!WsLiveV2Feeder.connected) {
      this.setTokensInQueue(
        mode,
        [...unsubscribedInstrumentKeys, ...changeModeInstrumentKeys[mode]],
        true,
      );
      return;
    }

    if (unsubscribedInstrumentKeys.length > 0) {
      this.unsubscribeScripFeed(unsubscribedInstrumentKeys, mode, guid);
      this.cleanupSubscriptionData(unsubscribedInstrumentKeys);
    }

    this.changeScripSubscriptionFeed(
      changeModeInstrumentKeys,
      guid,
    );
  };

  private changeScripSubscriptionFeed = (
    changeModeInstrumentKeys: ChangeModeSubscriptionData,
    guid?: string,
  ) => {
    if (changeModeInstrumentKeys.ltpc.length > 0) {
      this.changeScripFeedMode(
        changeModeInstrumentKeys.ltpc,
        MARKET_FEEDER_MODES.LTPC,
        guid,
      );
    }

    if (changeModeInstrumentKeys.option_chain.length > 0) {
      this.changeScripFeedMode(
        changeModeInstrumentKeys.option_chain,
        MARKET_FEEDER_MODES.OPTION_CHAIN,
        guid,
      );
    }

    if (changeModeInstrumentKeys.full.length > 0) {
      this.changeScripFeedMode(
        changeModeInstrumentKeys.full,
        MARKET_FEEDER_MODES.FULL,
        guid,
      );
    }

    if (changeModeInstrumentKeys.full_sr.length > 0) {
      this.changeScripFeedMode(
        changeModeInstrumentKeys.full_sr,
        MARKET_FEEDER_MODES.FULL_SR,
        guid,
      );
    }

    if (changeModeInstrumentKeys.full_d30.length > 0) {
      this.changeScripFeedMode(
        changeModeInstrumentKeys.full_d30,
        MARKET_FEEDER_MODES.FULL_D30,
        guid,
      );
    }
  }

  private convertToString = (value: number | undefined): string =>
    (typeof value !== 'undefined' ? value.toString() : '');

  private handleLiveDataResponse: WsHandler<
    MarketFeederV2.LiveDataDetails,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  > = async (message) => {
    TradingChartService.handleGetOHLCDataV2(message);

    const { data } = message;

    const liveData = await Promise.all(
      Object.entries(data.feeds).map(async ([uniqueId, value]) => {
        const { ltpc, ff, oc } = value;
        let formattedData;
        let mode: MARKET_FEEDER_MODES = MARKET_FEEDER_MODES.LTPC;

        if (ltpc) {
          formattedData = await this.handleResponse(
            ltpc,
            uniqueId,
            MARKET_FEEDER_MODES.LTPC,
          );
          mode = MARKET_FEEDER_MODES.LTPC;
        }

        if (oc) {
          formattedData = await this.handleResponse(
            oc,
            uniqueId,
            MARKET_FEEDER_MODES.OPTION_CHAIN,
          );
          mode = MARKET_FEEDER_MODES.OPTION_CHAIN;
        }

        if (ff) {
          formattedData = await this.handleResponse(
            ff,
            uniqueId,
            MARKET_FEEDER_MODES.FULL,
          );
          mode = MARKET_FEEDER_MODES.FULL;
        }

        return {
          formattedData,
          mode,
          instrumentKey: uniqueId,
        };
      }),
    );

    this.setFeederData(liveData);
  };

  getDomFormattedData(bidAskQuote: Quote[]): DOMData {
    const asks: DOMLevel[] = [];
    const bids: DOMLevel[] = [];

    bidAskQuote.forEach((quote) => {
      const { bidQ, bp, ap, askQ } = quote;
      asks.push({
        price: Number(this.convertToString(ap)),
        volume: Number(this.convertToString(askQ)),
      });
      bids.push({
        price: Number(this.convertToString(bp)),
        volume: Number(this.convertToString(bidQ)),
      });
    });

    return {
      snapshot: true,
      asks,
      bids,
    };
  }

  private updateQuotesData = async (
    uniqueId: string,
    formattedData: MarketFeederV2.FormattedFullFeed,
  ) => {
    const {
      close,
      lastTradedPrice,
      totalVolume,
      high,
      low,
      askPrice,
      buyPrice,
      open,
      bidAskQuote,
    } = formattedData;

    const formattedQuotesData: QuoteData[] = [];

    const scripData = await SearchService.searchByInstrumentKey(uniqueId);
    const exchangeToken = scripData?.exchangeToken || '';
    const isDomPanelVisible = broker.instance &&
      broker.instance.isDomPanelVisible();

    if (isDomPanelVisible && exchangeToken === this.domDetails.symbol) {
      const domFormattedResult: DOMData = this.getDomFormattedData(
        bidAskQuote,
      );
      this.domDetails.callback(domFormattedResult);
    }

    const { value, percentageChange } = FormatService.getLtpChange(
      lastTradedPrice,
      close,
      false,
      exchangeToken,
    );

    const unformattedValue = FormatService.unformatIndianNumber(value);

    const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
      scripData?.displayName || '',
      exchangeToken || '',
    );

    const percentageValue = FormatService.getPercentageValue(
      value, percentageChange,
    );

    const segment = scripData?.segment ||
      decomposeExchangeToken(exchangeToken).segment;

    const quoteData: QuoteData = {
      s: 'ok',
      n: exchangeToken,
      v: {
        ch: unformattedValue,
        chp: percentageValue,
        exchange: segment,
        lp: +lastTradedPrice,
        ask: FormatService.unformatIndianNumber(askPrice),
        bid: FormatService.unformatIndianNumber(buyPrice),
        open_price: Number(open),
        high_price: Number(high),
        low_price: Number(low),
        prev_close_price: +close,
        volume: Number(totalVolume) || 0,
        short_name: scripData?.displayName,
        description: `${segment}:${scripData?.displayName}`,
        // TODO: Remove after TV dev folks release the DOM crash bug fix
        typespecs: [],
      },
    };
    const scripDataWithTvExchangeToken = cloneDeep(quoteData);
    scripDataWithTvExchangeToken.n = tvExchangeToken;

    formattedQuotesData.push(quoteData);
    formattedQuotesData.push(scripDataWithTvExchangeToken);

    Object.values(this.quotesData).forEach(callback =>
      callback(formattedQuotesData),
    );
  }

  private handleResponse = async (
    data: LTPC | FullFeed | OptionChain,
    uniqueId: string,
    mode: MARKET_FEEDER_MODES,
  ) => {
    let formattedData;

    switch (mode) {
      case MARKET_FEEDER_MODES.LTPC:
        formattedData = this.parseLTPCtoFormattedLTPC(data as LTPC);
        break;

      case MARKET_FEEDER_MODES.OPTION_CHAIN:
        formattedData = this.parseOCtoFormattedOC(data as OptionChain);
        break;

      case MARKET_FEEDER_MODES.FULL:
        formattedData = this.parseFFtoFormattedFF(data as FullFeed, uniqueId);
        break;

      default:
        break;
    }

    this.updateQuotesData(
      uniqueId,
      formattedData as MarketFeederV2.FormattedFullFeed,
    );

    return Promise.resolve(formattedData);
  };

  private parseLTPCtoFormattedLTPC = (
    data: LTPC,
  ): MarketFeederV2.FormattedLTPC => ({
    close: this.convertToString(data?.cp),
    lastTradedPrice: this.convertToString(data?.ltp),
    lastTradedPrevious: this.convertToString(data?.ltp),
  });

  private parseOCtoFormattedOC = (
    data: OptionChain,
  ): MarketFeederV2.FormattedOptionChain => ({
    ...this.parseLTPCtoFormattedLTPC(data.ltpc as LTPC),
    openInterest: this.convertToString(data.eFeedDetails?.oi),
    lastTradedTime: this.convertToString(data.ltpc?.ltt),
    buyQuantity: this.convertToString(data.bidAskQuote?.bidQ),
    buyPrice: this.convertToString(data.bidAskQuote?.bp),
    askQuantity: this.convertToString(data.bidAskQuote?.askQ),
    askPrice: this.convertToString(data.bidAskQuote?.ap),
    averageTradingPrice: this.convertToString(data.eFeedDetails?.atp),
    totalVolume: this.convertToString(data.eFeedDetails?.vtt),
    underlyingLastTradingPrice: '', // didn't find this value in new Market format data
    totalBuyQuantity: this.convertToString(data.eFeedDetails?.tbq),
    totalSellQuantity: this.convertToString(data.eFeedDetails?.tsq),
    previousOpenInterest: this.convertToString(data.eFeedDetails?.poi),
    optionPrice: this.convertToString(data.optionGreeks?.op),
    underlyingLastTradedPrice: this.convertToString(data.optionGreeks?.up),
    impliedVolatility: this.convertToString(data.optionGreeks?.iv),
    delta: this.convertToString(data.optionGreeks?.delta),
    theta: this.convertToString(data.optionGreeks?.theta),
    gamma: this.convertToString(data.optionGreeks?.gamma),
    vega: this.convertToString(data.optionGreeks?.vega),
    rho: this.convertToString(data.optionGreeks?.rho),
  });

  private parseFFtoFormattedFF = (
    data: FullFeed,
    uniqueId: string,
  ): MarketFeederV2.FormattedFullFeed | MarketFeederV2.FormattedIndex => {
    if (this.isIndex(uniqueId)) {
      return {
        ...this.parseLTPCtoFormattedLTPC(data.indexFF?.ltpc as LTPC),
        ohlcData: data.indexFF?.marketOHLC?.ohlc,
        open: this.convertToString(data.indexFF?.marketOHLC?.ohlc[0]?.open),
        high: this.convertToString(data.indexFF?.marketOHLC?.ohlc[0]?.high),
        low: this.convertToString(data.indexFF?.marketOHLC?.ohlc[0]?.low),
        percentChange: '', // didn't find this value in new Market format data
        yearHigh: this.convertToString(data.indexFF?.yh),
        yearLow: this.convertToString(data.indexFF?.yl),
        numberOfUpMoves: '', // didn't find this value in new Market format data
        numberOfDownMoves: '', // didn't find this value in new Market format data
        marketCap: '', // didn't find this value in new Market format data
      };
    }

    const optionChainData: OptionChain = {
      ltpc: data.marketFF?.ltpc,
      optionGreeks: data.marketFF?.optionGreeks,
      eFeedDetails: data.marketFF?.eFeedDetails,
      bidAskQuote: undefined,
    };

    return {
      ...this.parseOCtoFormattedOC(optionChainData),
      bidAskQuote: data.marketFF?.marketLevel?.bidAskQuote,
      buyPrice: this.convertToString(
        data.marketFF?.marketLevel?.bidAskQuote[0]?.bp,
      ),
      askPrice: this.convertToString(
        data.marketFF?.marketLevel?.bidAskQuote[0]?.ap,
      ),
      ohlcData: data.marketFF?.marketOHLC?.ohlc,
      open: this.convertToString(data.marketFF?.marketOHLC?.ohlc[0]?.open),
      high: this.convertToString(data.marketFF?.marketOHLC?.ohlc[0]?.high),
      low: this.convertToString(data.marketFF?.marketOHLC?.ohlc[0]?.low),
      totalVolume: this.convertToString(data.marketFF?.eFeedDetails?.vtt),
      lastTradedQuantity: this.convertToString(data.marketFF?.ltpc?.ltq),
      volumeTradedToday: this.convertToString(data.marketFF?.eFeedDetails?.vtt),
      underlyingLastTradedPrice: '',
      lowerCircuit: this.convertToString(data.marketFF?.eFeedDetails?.lc),
      upperCircuit: this.convertToString(data.marketFF?.eFeedDetails?.uc),
    };
  };

  private isIndex = (uniqueId: string) => uniqueId.includes('INDEX');
}

const liveDataService = new LiveDataService();

export default liveDataService;
